import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

interface SortablePanelRowProps {
  id: string;
  children: ReactNode;
  /** Right-hand qty note, kept outside the row's own layout. */
  note?: ReactNode;
}

/**
 * One draggable panel row. Only the grip handle activates a drag — the
 * listeners go on `setActivatorNodeRef`, not the container — so the text
 * inputs and autocompletes inside `children` keep normal click and
 * drag-select behaviour.
 */
export default function SortablePanelRow({ id, children, note }: SortablePanelRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // x is zeroed so rows track the pointer vertically only. Cheaper than
  // pulling in @dnd-kit/modifiers just for restrictToVerticalAxis.
  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        type="button"
        aria-label="Reorder panel"
        className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none px-0.5"
      >
        <GripVertical size={14} style={{ color: 'var(--text-muted)' }} />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
      {note}
    </div>
  );
}
